export type Lang = 'en' | 'es'

export const translations = {
  en: {
    hero: {
      headline:
        'Finally — A Barbershop In Miami That Gets It Right Every Single Time',
    },
    openingLead: [
      'You already know what a bad haircut feels like.',
      'The uneven fade. The neckline that is slightly off. The stylist on their phone between strokes. The sure, I can do that followed by a result that looks nothing like what you asked for.',
      'And the worst part? You tip anyway. You say looks good. And you drive home quietly frustrated, already counting the days until it grows back out.',
      'This is not rare. This is the standard experience at most shops today — chain salons built for volume, not quality. Places that treat you like a number. People with clippers and no craft.',
      'You have been settling. And you deserve better.',
    ],
    greatBarber: {
      heading: 'What A Great Barber Actually Does For You',
      paragraphs: [
        'A great haircut is not just about hair.',
        'It is about walking into a meeting and knowing you look sharp. It is about the double-take from someone you love when you come home. It is about your kids seeing someone who takes care of themselves and learning what that looks like.',
        'It is about confidence — the quiet, unspoken kind that comes from knowing your appearance is handled.',
        'When you sit in the right chair, with the right barber, you do not leave thinking about your haircut. You leave thinking about everything you are about to go do — because you feel ready.',
        'That is the experience. That is what has been missing.',
      ],
    },
    pullQuotes: {
      quote1:
        'A bad haircut does not just look wrong. It feels wrong. All day.',
      quote2:
        'There is a reason your grandparents had one barber for thirty years. Find yours.',
      quote3: 'The fade does not lie. Neither do we.',
    },
    oldSolutions: {
      heading: 'Why The Old Solutions Keep Letting You Down',
      paragraphs: [
        'Let us be honest about what is out there:',
        'Chain salons rotate through staff like a revolving door. You never see the same person twice. Nobody knows your style. Nobody remembers your preferences. You are a ticket number, not a client.',
        'Budget walk-in shops are playing a volume game. Get you in, get you out, take the $15. They are not invested in your look. They are invested in the next customer.',
        'Overpriced salons charge three times more and still do not listen. They are built for upsells, not for getting your cut right.',
        'And the internet? Full of places that look great on Instagram but cannot consistently deliver what they photograph.',
        'The real reason people keep struggling is not bad luck. It is that most shops have forgotten what a barbershop is supposed to be.',
      ],
    },
    classicStandard: {
      heading: 'The Classic Barbershop Standard',
      paragraphs: [
        'There was a time when the barbershop was sacred.',
        'A place where the craft was respected. Where the barber knew you. Where you sat down, said a few words, and trusted completely — because you had been coming back for years and they had never let you down.',
        'That is not nostalgia. That is the standard we hold ourselves to every single day.',
        'At our classic barbershop on Curtiss Pkwy in Virginia Gardens, we do one thing — we make you look your best. Period.',
        'No gimmicks. No trendy add-ons. No rotating staff of strangers.',
        'Just precise cuts, clean shaves, and expert styling — delivered by barbers who treat their craft like it matters. Because it does.',
      ],
    },
    services: [
      {
        title: "MEN'S CUTS",
        description:
          'Every cut is tailored to your face shape, your style, and your life. Whether you want a clean taper, a classic side part, or a sharp modern fade — we deliver it with precision. Every time.',
      },
      {
        title: "WOMEN'S CUTS AND STYLING",
        description:
          'Sharp bobs, layered cuts, precision trims, and styles that actually hold. We listen to what you want and deliver exactly that — no upsells, no confusion. Just a great cut from someone who respects the craft.',
      },
      {
        title: 'STRAIGHT RAZOR SHAVES',
        description:
          'There is nothing like a real shave. Hot towel. Straight razor. Clean skin. It is not just grooming — it is a ritual. The kind of experience that reminds you what self-care actually feels like.',
      },
      {
        title: 'BEARD TRIMS AND SHAPING',
        description:
          'A great beard does not just grow — it is sculpted. We shape, trim, and line your beard to complement your face and match your style. Walk in with a beard. Walk out with the beard.',
      },
      {
        title: 'SCALP MICROPIGMENTATION (SMP)',
        description:
          'Bald spots, thinning hair, or fully bald and want the look of a fresh fade? Our in-house SMP specialist creates natural-looking hairlines and density that look like a real buzz cut. No surgery. No downtime. Just confidence restored.',
      },
      {
        title: 'KIDS ARE WELCOME',
        description:
          'Bring your kids. We are great with children and we understand how important their first great haircut is. Start them right. Start them here.',
      },
    ],
    gallery: {
      heading: 'Our Work Speaks For Itself',
    },
    smpGallery: {
      heading: 'SMP Before & After — Real Clients, Real Results',
      description:
        'Our in-house SMP specialist brings over 5 years of experience transforming hairlines and restoring density with precision pigment work. Whether you are dealing with bald spots, thinning hair, or want the look of a fresh buzz cut — the results speak for themselves. No surgery. No downtime. Just confidence restored.',
      before: 'Before',
      after: 'After',
    },
    hours: {
      locationHeading: 'Location',
      hoursHeading: 'Hours',
      addressLabel: 'Address:',
      phoneLabel: 'Phone:',
      amenitiesLabel: 'Amenities:',
      amenitiesValue: 'Restroom Available on site',
      days: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ],
      closed: 'Closed',
    },
    forYou: {
      heading: 'This Is For You If...',
      items: [
        'You are tired of inconsistent cuts from shops that do not know your name',
        'You want a barber who actually listens before picking up the clippers',
        'You are a parent who wants to bring your kids somewhere clean, welcoming, and great with children',
        'You miss the days when a barbershop felt like a place — not just a transaction',
        'You believe how you look on the outside reflects how you feel on the inside',
        'You live or work near Miami and want a shop you can actually rely on',
      ],
    },
    notForYou: {
      heading: 'This Is NOT For You If...',
      items: [
        'You want the cheapest possible cut with zero regard for quality',
        'You are fine with good enough and have no standards for your appearance',
        'You want a generic salon experience with trendy upsells and confusion',
      ],
      closing:
        'We are not for everyone. We are for the person who gives a damn.',
    },
    riskReversal: {
      heading: 'The Risk Reversal',
      paragraph:
        'Walking in for the first time is always a leap of faith — we get that. So here is our commitment to you: we will not rush you, we will not ignore you, and we will not send you home with a cut you are not proud of. If something is not right, say so before you leave the chair. We will fix it. That is the standard. That is the promise.',
    },
    urgency: {
      heading: 'Do Not Wait Until You Need It — Come In Before You Do',
      paragraphs: [
        'Here is the thing about great grooming: the best time to come in is before the big moment, not after.',
        'Before the job interview. Before the family photos. Before the date, the event, the promotion, the reunion.',
        'Because someone who looks prepared feels prepared. And someone who feels prepared performs differently.',
        'Our schedule fills up — especially Thursday through Saturday. If you want the chair, call ahead or walk in early.',
      ],
    },
    cta: {
      heading: 'Your Next Step Is Simple',
      callLabel: 'Call us at',
      walkLabel: 'Walk into',
      openLabel: 'Open',
      openHours: 'Tuesday-Friday 9AM-7PM | Saturday 9AM-5PM',
      closingLine1:
        'Come in. Sit down. Leave looking like the best version of yourself.',
      closingLine2: 'That is it. That is the whole promise.',
      ctaButton: 'CALL (305) 885-3240',
      callNow: 'CALL NOW',
      goNow: 'GO NOW',
    },
    faq: {
      heading: 'Common Questions',
      items: [
        {
          q: 'I already have a barber.',
          a: 'Great — so did most of our regulars before they sat in our chair. We are not asking you to switch. We are asking you to compare. One visit will tell you everything.',
        },
        {
          q: 'How do I know you will cut it the way I want?',
          a: 'You tell us exactly what you want before we pick up a single tool. We listen first. We cut second. That is how it should work.',
        },
        {
          q: "Do you cut women's hair too?",
          a: 'Absolutely. We do precision cuts, bobs, layers, and styling for women. Same craft, same attention to detail, same standard. Everyone deserves a great cut.',
        },
        {
          q: 'What is scalp micropigmentation (SMP)?',
          a: 'SMP is a non-surgical treatment where our in-house specialist uses micro-needles to deposit pigment on the scalp, creating the appearance of a natural hairline, density, or a fresh buzz cut look. It is ideal for bald spots, thinning hair, or a fully shaved head. No downtime, natural results.',
        },
        {
          q: 'Are you good with kids?',
          a: 'Absolutely. We are family-friendly and patient with younger clients. Bring your kids — we will make sure they leave looking sharp and feeling great about it.',
        },
        {
          q: 'What if I do not like the result?',
          a: 'Say something before you leave the chair. We will make it right. Your satisfaction is not optional — it is the whole point.',
        },
        {
          q: 'Are walk-ins welcome?',
          a: 'Yes — walk-ins are always welcome. For a guaranteed spot, especially on busy Fridays and Saturdays, call ahead to (305) 885-3240.',
        },
        {
          q: 'Where exactly are you located?',
          a: 'We are at 3964 Curtiss Pkwy, Virginia Gardens, FL 33166 — easily accessible from Miami and surrounding areas. Restroom on site.',
        },
      ],
    },
    footer: {
      tagline: 'Circle Barbershop — Virginia Gardens, FL',
    },
    barberAlt: 'Barber with straight razor',
  },
  es: {
    hero: {
      headline: 'Por Fin — Una Barberia En Miami Que Lo Hace Bien Cada Vez',
    },
    openingLead: [
      'Ya sabes lo que se siente un mal corte de pelo.',
      'El degradado desigual. La linea del cuello ligeramente torcida. El estilista en su telefono entre pasadas. El claro, puedo hacer eso seguido de un resultado que no se parece en nada a lo que pediste.',
      'Y lo peor? Dejas propina de todas formas. Dices se ve bien. Y manejas a casa en silencio, frustrado, contando los dias hasta que vuelva a crecer.',
      'Esto no es raro. Esta es la experiencia estandar en la mayoria de los salones hoy — cadenas construidas para volumen, no calidad. Lugares que te tratan como un numero. Gente con maquinas y sin oficio.',
      'Has estado conformandote. Y mereces algo mejor.',
    ],
    greatBarber: {
      heading: 'Lo Que Un Gran Barbero Realmente Hace Por Ti',
      paragraphs: [
        'Un gran corte de pelo no se trata solo del cabello.',
        'Se trata de entrar a una reunion sabiendo que te ves impecable. Se trata de esa doble mirada de alguien que amas cuando llegas a casa. Se trata de que tus hijos vean a alguien que se cuida y aprendan como se ve eso.',
        'Se trata de confianza — ese tipo callado, que no se dice, que viene de saber que tu apariencia esta resuelta.',
        'Cuando te sientas en la silla correcta, con el barbero correcto, no sales pensando en tu corte. Sales pensando en todo lo que vas a hacer — porque te sientes listo.',
        'Esa es la experiencia. Eso es lo que ha faltado.',
      ],
    },
    pullQuotes: {
      quote1: 'Un mal corte no solo se ve mal. Se siente mal. Todo el dia.',
      quote2:
        'Hay una razon por la que tus abuelos tuvieron un solo barbero por treinta anos. Encuentra el tuyo.',
      quote3: 'El degradado no miente. Nosotros tampoco.',
    },
    oldSolutions: {
      heading: 'Por Que Las Viejas Soluciones Siguen Fallandote',
      paragraphs: [
        'Seamos honestos sobre lo que hay alla afuera:',
        'Los salones de cadena rotan personal como puerta giratoria. Nunca ves a la misma persona dos veces. Nadie conoce tu estilo. Nadie recuerda tus preferencias. Eres un numero de turno, no un cliente.',
        'Los lugares economicos juegan al volumen. Te meten, te sacan, cobran los $15. No estan invertidos en tu imagen. Estan invertidos en el siguiente cliente.',
        'Los salones caros cobran el triple y aun asi no escuchan. Estan construidos para venderte extras, no para hacerte el corte bien.',
        'Y el internet? Lleno de lugares que se ven genial en Instagram pero no pueden entregar consistentemente lo que fotografian.',
        'La verdadera razon por la que la gente sigue luchando no es mala suerte. Es que la mayoria de los salones han olvidado lo que una barberia debe ser.',
      ],
    },
    classicStandard: {
      heading: 'El Estandar de la Barberia Clasica',
      paragraphs: [
        'Hubo un tiempo en que la barberia era sagrada.',
        'Un lugar donde el oficio era respetado. Donde el barbero te conocia. Donde te sentabas, decias unas palabras, y confiabas completamente — porque habias estado regresando por anos y nunca te habian fallado.',
        'Eso no es nostalgia. Ese es el estandar al que nos mantenemos cada dia.',
        'En nuestra barberia clasica en Curtiss Pkwy en Virginia Gardens, hacemos una cosa — hacemos que te veas lo mejor posible. Punto.',
        'Sin trucos. Sin complementos de moda. Sin personal rotativo de desconocidos.',
        'Solo cortes precisos, afeitadas limpias y estilizado experto — entregados por barberos que tratan su oficio como si importara. Porque importa.',
      ],
    },
    services: [
      {
        title: 'CORTES PARA HOMBRES',
        description:
          'Cada corte esta hecho a la medida de la forma de tu cara, tu estilo y tu vida. Ya sea que quieras un taper limpio, una raya clasica o un degradado moderno y afilado — lo entregamos con precision. Cada vez.',
      },
      {
        title: 'CORTES Y ESTILIZADO PARA MUJERES',
        description:
          'Bobs definidos, cortes en capas, recortes de precision y estilos que realmente se mantienen. Escuchamos lo que quieres y entregamos exactamente eso — sin ventas adicionales, sin confusion. Solo un gran corte de alguien que respeta el oficio.',
      },
      {
        title: 'AFEITADO CON NAVAJA',
        description:
          'No hay nada como una afeitada real. Toalla caliente. Navaja. Piel limpia. No es solo aseo — es un ritual. El tipo de experiencia que te recuerda lo que realmente se siente el cuidado personal.',
      },
      {
        title: 'RECORTE Y FORMA DE BARBA',
        description:
          'Una gran barba no solo crece — se esculpe. Damos forma, recortamos y delineamos tu barba para complementar tu cara y combinar con tu estilo. Entra con barba. Sal con la barba.',
      },
      {
        title: 'MICROPIGMENTACION CAPILAR (SMP)',
        description:
          'Zonas calvas, cabello fino, o completamente calvo y quieres el look de un degradado fresco? Nuestro especialista en SMP crea lineas de cabello y densidad de aspecto natural que parecen un buzz cut real. Sin cirugia. Sin tiempo de recuperacion. Solo confianza restaurada.',
      },
      {
        title: 'NINOS BIENVENIDOS',
        description:
          'Trae a tus hijos. Somos excelentes con ninos y entendemos lo importante que es su primer gran corte. Empiezalos bien. Empiezalos aqui.',
      },
    ],
    gallery: {
      heading: 'Nuestro Trabajo Habla Por Si Solo',
    },
    smpGallery: {
      heading: 'SMP Antes y Despues — Clientes Reales, Resultados Reales',
      description:
        'Nuestro especialista en SMP trae mas de 5 anos de experiencia transformando lineas de cabello y restaurando densidad con trabajo de pigmento de precision. Ya sea que tengas zonas calvas, cabello fino, o quieras el look de un buzz cut fresco — los resultados hablan por si solos. Sin cirugia. Sin tiempo de recuperacion. Solo confianza restaurada.',
      before: 'Antes',
      after: 'Despues',
    },
    hours: {
      locationHeading: 'Ubicacion',
      hoursHeading: 'Horario',
      addressLabel: 'Direccion:',
      phoneLabel: 'Telefono:',
      amenitiesLabel: 'Servicios:',
      amenitiesValue: 'Bano disponible en el local',
      days: [
        'Lunes',
        'Martes',
        'Miercoles',
        'Jueves',
        'Viernes',
        'Sabado',
        'Domingo',
      ],
      closed: 'Cerrado',
    },
    forYou: {
      heading: 'Esto Es Para Ti Si...',
      items: [
        'Estas cansado de cortes inconsistentes de lugares que no saben tu nombre',
        'Quieres un barbero que realmente escuche antes de agarrar las herramientas',
        'Eres padre o madre y quieres llevar a tus hijos a un lugar limpio, acogedor y bueno con ninos',
        'Extranas los dias cuando la barberia se sentia como un lugar — no solo una transaccion',
        'Crees que como te ves por fuera refleja como te sientes por dentro',
        'Vives o trabajas cerca de Miami y quieres un lugar en el que realmente puedas confiar',
      ],
    },
    notForYou: {
      heading: 'Esto NO Es Para Ti Si...',
      items: [
        'Quieres el corte mas barato posible sin importar la calidad',
        'Te conformas con suficientemente bueno y no tienes estandares para tu apariencia',
        'Quieres una experiencia de salon generico con ventas adicionales de moda y confusion',
      ],
      closing: 'No somos para todos. Somos para quien le importa.',
    },
    riskReversal: {
      heading: 'La Garantia',
      paragraph:
        'Entrar por primera vez siempre es un acto de fe — lo entendemos. Asi que aqui esta nuestro compromiso contigo: no te vamos a apurar, no te vamos a ignorar, y no te vamos a mandar a casa con un corte del que no estes orgulloso. Si algo no esta bien, dilo antes de levantarte de la silla. Lo arreglaremos. Ese es el estandar. Esa es la promesa.',
    },
    urgency: {
      heading: 'No Esperes Hasta Que Lo Necesites — Ven Antes',
      paragraphs: [
        'Esto es lo importante del buen aseo: el mejor momento para venir es antes del gran momento, no despues.',
        'Antes de la entrevista de trabajo. Antes de las fotos familiares. Antes de la cita, el evento, la promocion, la reunion.',
        'Porque quien se ve preparado se siente preparado. Y quien se siente preparado rinde diferente.',
        'Nuestro horario se llena — especialmente de jueves a sabado. Si quieres la silla, llama con anticipacion o ven temprano.',
      ],
    },
    cta: {
      heading: 'Tu Siguiente Paso Es Simple',
      callLabel: 'Llamanos al',
      walkLabel: 'Visitanos en',
      openLabel: 'Abierto',
      openHours: 'Martes-Viernes 9AM-7PM | Sabado 9AM-5PM',
      closingLine1:
        'Entra. Sientate. Sal viendote como la mejor version de ti.',
      closingLine2: 'Eso es todo. Esa es toda la promesa.',
      ctaButton: 'LLAMA AL (305) 885-3240',
      callNow: 'LLAMA AHORA',
      goNow: 'IR AHORA',
    },
    faq: {
      heading: 'Preguntas Frecuentes',
      items: [
        {
          q: 'Ya tengo un barbero.',
          a: 'Genial — la mayoria de nuestros clientes regulares tambien tenian uno antes de sentarse en nuestra silla. No te pedimos que cambies. Te pedimos que compares. Una visita te dira todo.',
        },
        {
          q: 'Como se que lo cortaran como yo quiero?',
          a: 'Nos dices exactamente lo que quieres antes de que agarremos una sola herramienta. Escuchamos primero. Cortamos despues. Asi es como debe funcionar.',
        },
        {
          q: 'Tambien cortan cabello de mujer?',
          a: 'Absolutamente. Hacemos cortes de precision, bobs, capas y estilizado para mujeres. El mismo oficio, la misma atencion al detalle, el mismo estandar. Todos merecen un gran corte.',
        },
        {
          q: 'Que es la micropigmentacion capilar (SMP)?',
          a: 'SMP es un tratamiento no quirurgico donde nuestro especialista usa micro-agujas para depositar pigmento en el cuero cabelludo, creando la apariencia de una linea de cabello natural, densidad, o el look de un buzz cut fresco. Es ideal para zonas calvas, cabello fino, o cabeza completamente rapada. Sin tiempo de recuperacion, resultados naturales.',
        },
        {
          q: 'Son buenos con ninos?',
          a: 'Absolutamente. Somos familiares y pacientes con clientes jovenes. Trae a tus hijos — nos aseguraremos de que salgan viendose bien y sintiendose genial.',
        },
        {
          q: 'Que pasa si no me gusta el resultado?',
          a: 'Dilo antes de levantarte de la silla. Lo arreglaremos. Tu satisfaccion no es opcional — es todo el punto.',
        },
        {
          q: 'Aceptan sin cita?',
          a: 'Si — siempre aceptamos sin cita. Para un lugar garantizado, especialmente los viernes y sabados ocupados, llama con anticipacion al (305) 885-3240.',
        },
        {
          q: 'Donde estan ubicados exactamente?',
          a: 'Estamos en 3964 Curtiss Pkwy, Virginia Gardens, FL 33166 — facilmente accesible desde Miami y areas circundantes. Bano disponible en el local.',
        },
      ],
    },
    footer: {
      tagline: 'Circle Barbershop — Virginia Gardens, FL',
    },
    barberAlt: 'Barbero con navaja',
  },
} as const

export type Translations = (typeof translations)['en']
